import React, { useState, useEffect, useRef } from 'react';
import { closeImportModalFile } from './helper';
import { exportOrdersData } from '../Services/importExport';
import { postApi } from '../Services/apiService';
import { toast } from 'react-toastify';

function Import({
  setImportFile,
  importFile,
  searchQuery,
  fetchData,
  setShowImportModal,
  routeEnd,
  routeexport,
  type,
  queryParams
}) {
  const [isImporting, setIsImporting] = useState(false);
  const [importErrors, setImportErrors] = useState({
    file: '',
    general: '',
    detailed: []
  });
  const [partiallyImport, setPartiallyImport] = useState(false);
  const fileInputRef = useRef(null);

  // Keyboard event handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Close modal on Escape key
      if (e.key === 'Escape') {
        closeImportModalFile(setShowImportModal, setImportFile, setImportErrors);
      }
      // Trigger import on Enter key if conditions are met
      else if (e.key === 'Enter' || 'return' && !isImporting && importFile && !partiallyImport) {
        if(!isImporting){
        handleImportSubmit();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isImporting, importFile, partiallyImport]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/octet-stream'
    ];

    const isValidType = validTypes.includes(file.type) ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls');

    if (!isValidType) {
      setImportErrors({ ...importErrors, file: 'Only Excel files (.xlsx, .xls) are allowed', detailed: [] });
      setImportFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (file.size > (type==='Product' ?(4 * 1024 * 1024) :(2*1024*1024))) {
      setImportErrors({ ...importErrors, file: 'File size should not exceed 2MB', detailed: [] });
      setImportFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setImportFile(file);
    setImportErrors({ ...importErrors, file: '', detailed: [] });
  };

  const downloadSampleFile = async () => {
    try {
      if (type == 'Direct Inout') {
        const fileUrl = window.getPublicUrl('/assets/sample-files/direct-inout-sample1.xlsx');

        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', 'direct-inout-sample.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        await exportOrdersData(
          routeexport,
          { is_sample: 1, ...(queryParams && Object.keys(queryParams).length > 0 && queryParams) },
          `${type || 'sample'}.xlsx`
        );
      }
    } catch (error) {
      toast.error('Failed to download sample file');
      console.error('Error downloading sample:', error);
    }
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      setImportErrors({
        file: 'Please select a file to import',
        general: '',
        detailed: []
      });
      return;
    }

    try {
      setIsImporting(true);
      setImportErrors({ file: '', general: '', detailed: [] });

      const formData = new FormData();
      formData.append('file', importFile);

      const response = await postApi(routeEnd, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 600000
      });

      if (response?.success) {
        if (response?.data?.errors?.length > 0) {
          let formattedErrors;
          {type==='Direct Inout' ?
          formattedErrors = response.data.errors.flatMap(error =>
            error.errors.map(err => `Sheet ${error.sheet} Row ${error.row}: ${error.attribute} - ${err}`))            
            :
          formattedErrors = response.data.errors.flatMap(error =>
              
              error.errors.map(err => `Row ${error.row}: ${error.attribute} - ${err}`))
          }
          

          setImportErrors(prev => ({
            ...prev,
            detailed: formattedErrors.length > 0 ? formattedErrors : ['Failed to process import'],
          }));
          toast.success(response.msg || `${type || 'Data'} imported successfully`);
          setPartiallyImport(true);
          // Reset file input and clear file state to allow re-selecting files
          setImportFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } else {
          toast.success(response.msg || `${type || 'Data'} imported successfully`);
          fetchData(searchQuery);
          // Reset file input before closing
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          closeImportModalFile(setShowImportModal, setImportFile, setImportErrors);
        }
      } else {
        let formattedErrors = [];
        // Add null/undefined checks for response.data
        if (response?.data?.errors && Array.isArray(response.data.errors)) {
          {type==='Direct Inout' ?
          formattedErrors = response.data.errors.flatMap(error =>
            error.errors.map(err => `Sheet ${error.sheet} Row ${error.row}: ${error.attribute} - ${err}`))            
            :
          formattedErrors = response.data.errors.flatMap(error =>
              
              error.errors.map(err => `Row ${error.row}: ${error.attribute} - ${err}`))
          }
        }

        setImportErrors(prev => ({
          ...prev,
          detailed: formattedErrors.length > 0 ? formattedErrors : ['Failed to process import'],
        }));
        // Reset file input and clear file state to allow re-selecting files
        setImportFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error('Import error:', error);
      const errorMsg = error.response?.data?.msg ||
        error.message ||
        `Failed to import ${type || 'data'}`;
      setImportErrors(prev => ({
        ...prev,
        general: errorMsg,
        detailed: []
      }));
      // Reset file input and clear file state on error to allow re-selecting files
      setImportFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="modal-overlay file-upload-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Import {type || 'Data'}</h3>
          <img
            src={window.getPublicUrl('/assets/images/close.svg')}
            onClick={() => {if(!isImporting){closeImportModalFile(setShowImportModal, setImportFile, setImportErrors)}}}
            className="close"
            alt="Close"
          />
        </div>
        <div className="modal-body mb-0 p-0">
          <div className="row">
            <div className="col-sm-12">
              <div className="form-group">
                <div className="file-upload-wrapper">
                  <label>
                    <form>
                      <img
                        src={window.getPublicUrl('/assets/images/file-download-import-icon.svg')}
                        alt="File import"
                      />
                      <p>Select File</p>
                      {!partiallyImport &&
                        <input
                          type="file"
                          id="importFile"
                          ref={fileInputRef}
                          accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          onChange={handleFileChange}
                          className={`file-upload-input ${importErrors.file ? 'is-invalid' : ''}`}
                        />
                      }
                    </form>
                  </label>
                  {importErrors.file && (
                    <div className="invalid-feedback d-block" style={{ color: 'red', marginTop: '5px' }}>
                      <i className="fas fa-exclamation-circle me-2"></i>
                      {importErrors.file}
                    </div>
                  )}
                  {importFile && (
                    <div className="file-info mt-4">
                      <p className='selected-name mb-0'>
                        <strong className='me-1'>Selected File: </strong>
                        {importFile.name} ({Math.round(importFile.size / 1024)} KB)
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              <div className="download-sample">
                <div className="d-flex justify-content-between pt-3 pb-2">
                  <p>Supported format: .xlsx</p>
                  <p>(Max size: {type==='Product'?4:2}MB)</p>
                </div>
                <button
                  className="btn btn-link p-0 text-primary"
                  onClick={downloadSampleFile}
                >
                  Download Sample File
                </button>
              </div>
            </div>
          </div>
          {(importErrors.general || importErrors.detailed?.length > 0) && (
            <div className="error-message mt-3 p-3 bg-light rounded">
              {importErrors.general && (
                <div className="text-danger mb-2">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {importErrors.general}
                </div>
              )}
              {importErrors.detailed?.length > 0 && (
                <ul className="mb-0" >
                  {importErrors.detailed.map((error, index) => (
                    <li key={index} className="text-danger">
                      {error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          {!partiallyImport &&
            <button
              className="reset"
              onClick={() => closeImportModalFile(setShowImportModal, setImportFile, setImportErrors)}
              disabled={isImporting}
            >
              Cancel
            </button>
          }
          {partiallyImport ?
            <button
              className="save mt-0"
              onClick={() => closeImportModalFile(setShowImportModal, setImportFile, setImportErrors)}
              disabled={isImporting}
            >
              Close
            </button>
            :
            <button
              className="save mt-0"
              onClick={handleImportSubmit}
              disabled={isImporting || !importFile}
            >
              {isImporting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Importing...
                </>
              ) : (
                'Import'
              )}
            </button>
          }
        </div>
      </div>
    </div>
  );
}

export default Import;